import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import NotificationsIndexPage from '../notifications_index_page/NotificationsIndexPage';
import { EmptyState } from '../empty_state/EmptyState';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { getStateSliceProp } from '../../utils/DataAccessUtils';
import {
	userNotificationsRestCrudThunksForBuyer,
	userNotificationsRestCrudThunksForSupplier,
} from '../../thunks/user_notifications_thunks';
import {
	REMINDER_NOTIFICATIONS_COUNT,
	UNREAD_NOTIFICATION_TYPES,
	UNREAD_REMINDER_NOTIFICATIONS,
} from '../../utils/NotificationUtils';
import { isWorkOrderRemindersEnabled } from '../../data/workOrderStarLabels';
import useWorkOrderStarSocket, {
	isReminderDueOrReconnect,
} from '../../hooks/useWorkOrderStarSocket';

interface CollectionPagination {
	current?: number;
	pageSize?: number;
}
interface CollectionSorting {
	sort_by?: string;
	order?: string;
}
type CollectionFilters = Record<string, unknown>;
// The parts of a user_notifications target collection this tab reads back to refetch it as it is.
interface NotificationsCollectionState {
	pagination?: CollectionPagination;
	sorting?: CollectionSorting;
	filters?: CollectionFilters;
}
type UserNotificationsSlice = Record<string, NotificationsCollectionState | unknown>;

type FetchUserNotificationsCount = (
	params: CollectionFilters,
	countName?: string | null
) => Promise<unknown>;
type FetchUserNotifications = (
	targetCollectionName: string,
	pagination: CollectionPagination,
	sorting: CollectionSorting,
	filters: CollectionFilters
) => Promise<unknown>;

// Page 1 at the table's default size: where a refresh starts before the table has stored a page.
const FIRST_PAGE: CollectionPagination = { current: 1, pageSize: 10 };

interface NotificationsRemindersProps {
	userType: string;
	userNotifications: UserNotificationsSlice;
	fetchUserNotificationsCount: FetchUserNotificationsCount;
	fetchUserNotifications: FetchUserNotifications;
}

// The Inbox's Reminders tab: the important notifications sent when a work order reminder goes off.
const NotificationsReminders = ({
	userType,
	userNotifications,
	fetchUserNotificationsCount,
	fetchUserNotifications,
}: NotificationsRemindersProps) => {
	// When a reminder comes due, reload the list as the user has it (page, sort, All/Unread), so
	// the new notification shows without resetting their filter or selection. The collection only
	// stores those once a load succeeds, so while the first load is running (or after it failed)
	// refetch with the tab's own filters from page 1 — never with none, which lists everything.
	useWorkOrderStarSocket((message) => {
		if (!isReminderDueOrReconnect(message)) return;
		const filters: CollectionFilters = getStateSliceProp(
			'filters',
			REMINDER_NOTIFICATIONS_COUNT,
			userNotifications
		);
		const hasLoaded = Object.keys(filters).length > 0;
		fetchUserNotifications(
			REMINDER_NOTIFICATIONS_COUNT,
			hasLoaded
				? getStateSliceProp('pagination', REMINDER_NOTIFICATIONS_COUNT, userNotifications)
				: FIRST_PAGE,
			getStateSliceProp('sorting', REMINDER_NOTIFICATIONS_COUNT, userNotifications),
			hasLoaded ? filters : UNREAD_REMINDER_NOTIFICATIONS
		);
	});

	// The tab is hidden with reminders off (Work-Order-Reminders); guard the direct URL too.
	if (!isWorkOrderRemindersEnabled()) {
		return <Redirect to={`/${userType}/inbox/overview/important`} />;
	}

	// Reading a reminder lowers both this tab's count and the Important tab's.
	const handleNotificationClick = () => {
		fetchUserNotificationsCount(UNREAD_REMINDER_NOTIFICATIONS, REMINDER_NOTIFICATIONS_COUNT);
		fetchUserNotificationsCount(UNREAD_NOTIFICATION_TYPES, 'TASKS_NOTIFICATIONS');
	};

	return (
		<NotificationsIndexPage
			showFilters={false}
			preAppliedFilters={UNREAD_REMINDER_NOTIFICATIONS}
			targetCollectionName={REMINDER_NOTIFICATIONS_COUNT}
			onNotificationClick={handleNotificationClick}
			emptyState={
				<EmptyState
					graphic={
						<img
							className="mb-xs"
							src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
							alt=""
						/>
					}
					headline={"You don't have any reminders."}
					body={null}
				/>
			}
		/>
	);
};

const mapStateToProps = (state) => ({
	userType: state.session.userType,
	userNotifications: state.user_notifications,
});

// Binds the fetches to the session's role once, so the component never picks between them.
const mergeProps = (stateProps, { dispatch }) => {
	const thunks =
		stateProps.userType === ROLE_TYPES.BUYER
			? userNotificationsRestCrudThunksForBuyer
			: userNotificationsRestCrudThunksForSupplier;
	return {
		...stateProps,
		fetchUserNotificationsCount: (params, countName = null) =>
			dispatch(thunks.countBy(params, countName)),
		fetchUserNotifications: (targetCollectionName, pagination, sorting, filters) =>
			dispatch(thunks.read({}, targetCollectionName, pagination, sorting, filters)),
	};
};

export default connect(
	mapStateToProps,
	(dispatch) => ({ dispatch }),
	mergeProps
)(NotificationsReminders);
